/**
 * Session 29 — Monotonic-state-aware merge utilities for realtime sync.
 *
 * PRINCIPLE: State transitions in the restaurant lifecycle are monotonic
 * (they only move forward). An entity at a "more advanced" state is always
 * newer than one at a "less advanced" state, regardless of whether the
 * source is a WS event or an HTTP snapshot.
 *
 * MERGE RULE: Always keep the MORE ADVANCED state from either source.
 * This prevents stale HTTP snapshots from reverting WS-applied mutations,
 * while still allowing server-confirmed state advances to be applied.
 *
 * Session 29 — CRITICAL FIX: Items now use stable `id` fields (UUID for
 * new orders, positional `{orderId}:idx:{N}` for legacy). The previous
 * approach of `item.id || item._id || ""` collapsed ALL items into one
 * key ("") during merge, corrupting the items array and causing "0 items"
 * on Runner, broken item-level updates on Staff, and delivery misattribution.
 *
 * Used by: fetchDashboard (Staff), fetchReadyOrders (Runner),
 *          fetchOrders (Kitchen/Bar), and the safety-net polls.
 */

// ─── Order identity (Session 31.24) ─────────────────────────────────
// One logical order can be represented by up to three ids at once:
//   • `id`          — the server ObjectId (cloud copy)
//   • `_local_id`   — the client's optimistic `local-…` id
//   • `_change_id`  — the event's change id (shared by every copy)
// A ticket that arrived over the LAN mesh uses the optimistic id; its
// cloud copy uses the ObjectId but carries `_local_id` + `_change_id`.
// Matching on ANY overlap lets us collapse the two into one, regardless
// of which arrived first or whether their change_ids happened to differ.
export function orderIdentityKeys(order) {
  if (!order) return [];
  return [order.id, order._local_id, order._change_id]
    .filter((v) => v !== undefined && v !== null && v !== "")
    .map(String);
}

export function sameOrderIdentity(a, b) {
  if (!a || !b) return false;
  const bKeys = new Set(orderIdentityKeys(b));
  return orderIdentityKeys(a).some((k) => bKeys.has(k));
}

// Prefer the server copy (a real id, i.e. NOT a `local-…` placeholder)
// when collapsing two representations of the same order.
function _preferServerCopy(a, b) {
  const aLocal = String(a?.id || "").startsWith("local-");
  const bLocal = String(b?.id || "").startsWith("local-");
  if (aLocal && !bLocal) return b;
  if (bLocal && !aLocal) return a;
  return b; // same kind → the later one wins (newer snapshot)
}

// Collapse any entries that share an identity key. Keeps first-seen order.
export function dedupeByIdentity(orders) {
  const out = [];
  for (const o of orders || []) {
    if (!o) continue;
    const idx = out.findIndex((x) => sameOrderIdentity(x, o));
    if (idx === -1) out.push(o);
    else out[idx] = _preferServerCopy(out[idx], o);
  }
  return out;
}

// ─── State priority maps (higher = more advanced) ────────────────────
const SESSION_STATUS_PRIORITY = {
  open: 0,
  closed: 1,
};

// Session 46 — TERMINAL states (voided / rejected / cancelled / abandoned)
// outrank everything. Before this fix `voided` was missing from the map,
// so it fell back to priority 0 (same as `pending`): a server snapshot
// that said "voided" LOST to a local copy that still said "confirmed" or
// "completed", and the void popped straight back onto the table until
// the operator signed out. A void is irreversible in this system (there
// is no un-void endpoint), so it must always win the merge.
const ORDER_STATUS_PRIORITY = {
  pending: 0,
  confirmed: 1,
  ready: 2,
  delivered: 3,
  completed: 4,
  cancelled: 9,
  rejected: 9,
  abandoned: 9,
  voided: 10,
};

const TERMINAL_ORDER_STATUSES = new Set(["voided", "cancelled", "rejected", "abandoned"]);

export function isTerminalOrderStatus(status) {
  return TERMINAL_ORDER_STATUSES.has(String(status || "").toLowerCase());
}

const ITEM_STATUS_PRIORITY = {
  "": 0,
  pending: 0,
  confirmed: 1,
  ready: 2,
  delivered: 3,
};

function statusPriority(status, map) {
  if (status == null) return 0;
  return map[String(status).toLowerCase()] ?? 0;
}

/**
 * Session 29 — Robust item identity. Prefers the stable `id` field
 * set by the backend (UUID for new orders, `{orderId}:idx:{N}` for legacy).
 * Falls back to _id, then to empty string (which triggers positional merge).
 */
function itemId(item) {
  const id = item.id || item._id || "";
  return String(id);
}

/**
 * Session 29 — Check if an id is "empty" / non-unique (blank or missing).
 * When ALL items share the same empty id, we must fall back to positional
 * merging to avoid collapsing N items into 1.
 */
function isEmptyId(id) {
  return !id || id === "" || id === "undefined" || id === "null";
}

// ─── Item-level merge ────────────────────────────────────────────────
/**
 * Merge two arrays of items. For items present in both, keep the one
 * with the more advanced status. Items only in one source are kept.
 * Voided items: server is authoritative (void can only be set by backend).
 *
 * Session 29 — If items have stable IDs, merge by ID (O(n) map-based).
 * If items lack IDs (legacy edge case), use POSITIONAL merge (index-based)
 * to prevent array collapse.
 */
export function mergeItems(localItems, serverItems) {
  const localArr = localItems || [];
  const serverArr = serverItems || [];

  // Session 29 — Detect if items have usable IDs. If ALL server items
  // have empty IDs, fall back to positional merge.
  const serverHasIds = serverArr.length > 0 && serverArr.some((i) => !isEmptyId(itemId(i)));
  const localHasIds = localArr.length > 0 && localArr.some((i) => !isEmptyId(itemId(i)));

  if (!serverHasIds && !localHasIds) {
    // POSITIONAL MERGE: match items by array index. This is the safe
    // fallback for legacy orders where all items have id="".
    return mergeItemsByPosition(localArr, serverArr);
  }

  // ID-BASED MERGE: standard map-based approach
  const serverMap = new Map();
  for (const si of serverArr) {
    const id = itemId(si);
    if (!isEmptyId(id)) {
      serverMap.set(id, si);
    }
  }
  const localMap = new Map();
  for (const li of localArr) {
    const id = itemId(li);
    if (!isEmptyId(id)) {
      localMap.set(id, li);
    }
  }

  const merged = [];
  const seen = new Set();

  // Process all server items (by id when available, positional for empties)
  for (let idx = 0; idx < serverArr.length; idx++) {
    const serverItem = serverArr[idx];
    const id = itemId(serverItem);

    if (isEmptyId(id)) {
      // Fallback: use positional match for this item
      const localItem = idx < localArr.length ? localArr[idx] : null;
      merged.push(mergeOneItem(localItem, serverItem));
      continue;
    }

    seen.add(id);
    const localItem = localMap.get(id);
    merged.push(mergeOneItem(localItem, serverItem));
  }

  // Add local-only items (from WS events not yet in server snapshot)
  for (const localItem of localArr) {
    const id = itemId(localItem);
    if (!isEmptyId(id) && !seen.has(id)) {
      merged.push(localItem);
    }
  }

  return merged;
}

/**
 * Session 29 — Positional merge: match items by array index.
 * Safe for legacy items without IDs.
 */
function mergeItemsByPosition(localArr, serverArr) {
  const merged = [];
  const maxLen = Math.max(localArr.length, serverArr.length);

  for (let i = 0; i < maxLen; i++) {
    const localItem = i < localArr.length ? localArr[i] : null;
    const serverItem = i < serverArr.length ? serverArr[i] : null;
    merged.push(mergeOneItem(localItem, serverItem));
  }

  return merged;
}

/**
 * Session 29 — Merge a single item pair (local vs server).
 * Keeps the more advanced status. Server is authoritative for voids.
 */
function mergeOneItem(localItem, serverItem) {
  if (!localItem) return serverItem;
  if (!serverItem) return localItem;

  // Session 46 — a void is MONOTONIC: once either copy says the line is
  // voided, the merged line is voided. Previously only "server voided,
  // local not" was handled; the opposite case (this device voided the
  // line — optimistically or via a WS event — and a stale poll arrived a
  // moment later saying it was still live) returned the server copy and
  // silently UN-voided the line. That is the "voided items pop right
  // back" report. There is no un-void endpoint, so voided always wins.
  if (serverItem.voided && !localItem.voided) {
    return serverItem;
  }
  if (localItem.voided && !serverItem.voided) {
    return {
      ...serverItem,
      voided: true,
      voided_at: localItem.voided_at || serverItem.voided_at || null,
      voided_by: localItem.voided_by || serverItem.voided_by || null,
      void_reason: localItem.void_reason || serverItem.void_reason || null,
      // A partial-quantity void shrinks the line; keep the smaller qty.
      quantity: Math.min(Number(localItem.quantity) || 0, Number(serverItem.quantity) || 0) || serverItem.quantity,
    };
  }
  if (localItem.voided && serverItem.voided) {
    return { ...serverItem, ready_at: localItem.ready_at || serverItem.ready_at };
  }
  // Session 46 — partial-quantity voids reduce `quantity`; a stale copy can
  // only ever carry a LARGER quantity, so the smaller one is the truth.
  const lq = Number(localItem.quantity);
  const sq = Number(serverItem.quantity);
  const qtyPatch = (Number.isFinite(lq) && Number.isFinite(sq) && lq > 0 && lq < sq) ? { quantity: lq } : null;
  // Keep the more advanced status
  const localP = statusPriority(localItem.status, ITEM_STATUS_PRIORITY);
  const serverP = statusPriority(serverItem.status, ITEM_STATUS_PRIORITY);
  if (localP > serverP) {
    // Local is more advanced — keep it, but merge non-status fields from server
    return { ...serverItem, ...(qtyPatch || {}), status: localItem.status, ready_at: localItem.ready_at || serverItem.ready_at };
  } else if (serverP > localP) {
    // Server is more advanced
    return qtyPatch ? { ...serverItem, ...qtyPatch } : serverItem;
  } else {
    // Same status — prefer server (it has the latest non-status fields)
    return { ...serverItem, ...(qtyPatch || {}), ready_at: localItem.ready_at || serverItem.ready_at };
  }
}

// ─── Order-level merge ───────────────────────────────────────────────
/**
 * Merge a local order with a server order. The more advanced status wins.
 * Items are merged recursively. Server-only fields (timestamps, metadata)
 * are always taken from server if the server order is at least as advanced.
 */
export function mergeOrder(localOrder, serverOrder) {
  if (!localOrder) return serverOrder;
  if (!serverOrder) return localOrder;

  // Session 46 — an order-level void (status "voided" OR the `voided`
  // flag) is terminal and wins from either side. See ORDER_STATUS_PRIORITY.
  const localVoided = localOrder.voided === true || isTerminalOrderStatus(localOrder.status);
  const serverVoided = serverOrder.voided === true || isTerminalOrderStatus(serverOrder.status);
  if (serverVoided && !localVoided) {
    return { ...serverOrder, items: mergeItems(localOrder.items, serverOrder.items) };
  }
  if (localVoided && !serverVoided) {
    return {
      ...serverOrder,
      ...localOrder,
      status: isTerminalOrderStatus(localOrder.status) ? localOrder.status : "voided",
      voided: true,
      voided_at: localOrder.voided_at || serverOrder.voided_at || null,
      items: mergeItems(localOrder.items, serverOrder.items),
    };
  }

  const localP = statusPriority(localOrder.status, ORDER_STATUS_PRIORITY);
  const serverP = statusPriority(serverOrder.status, ORDER_STATUS_PRIORITY);

  // If local is strictly more advanced, keep entire local order
  // (but still merge items for safety)
  if (localP > serverP) {
    const items = mergeItems(localOrder.items, serverOrder.items);
    // Session 46 — if the merge surfaced voided lines the local copy did
    // not know about, the local money fields are stale (too high): take
    // the server's totals, which were recomputed by the void.
    const localVoids = (localOrder.items || []).filter((it) => it && it.voided).length;
    const mergedVoids = items.filter((it) => it && it.voided).length;
    const money = mergedVoids > localVoids
      ? { total: serverOrder.total, subtotal: serverOrder.subtotal, vat_total: serverOrder.vat_total, total_items: serverOrder.total_items }
      : {};
    return {
      ...localOrder,
      ...money,
      items,
    };
  }

  // If server is strictly more advanced, use server but preserve
  // any item-level advances from local
  if (serverP > localP) {
    return {
      ...serverOrder,
      items: mergeItems(localOrder.items, serverOrder.items),
    };
  }

  // Same status — use server as base (has freshest metadata),
  // but merge items to preserve any local item advances
  return {
    ...serverOrder,
    // Preserve local fields that may have been set by WS but not yet in server
    kitchen_acknowledged_at: localOrder.kitchen_acknowledged_at || serverOrder.kitchen_acknowledged_at,
    bar_acknowledged_at: localOrder.bar_acknowledged_at || serverOrder.bar_acknowledged_at,
    ready_at: localOrder.ready_at || serverOrder.ready_at,
    items: mergeItems(localOrder.items, serverOrder.items),
  };
}

// ─── Session-level merge ─────────────────────────────────────────────
/**
 * Session 31.18 — Does `serverOrders` already contain the cloud copy of the
 * optimistic `localOrder`? The server stamps `_local_id` (the client's
 * `local-…` id) and `_change_id` on the persisted order, so either match
 * means the local placeholder must NOT be kept alongside it — otherwise the
 * table detail + receipt show every line twice and the total doubles.
 */
export function serverHasLocalOrder(localOrder, serverOrders) {
  if (!localOrder) return false;
  const lid = localOrder.id ? String(localOrder.id) : "";
  const cid = localOrder._change_id ? String(localOrder._change_id) : "";
  if (!lid && !cid) return false;
  for (const so of serverOrders || []) {
    if (!so || so === localOrder) continue;
    if (lid && so._local_id && String(so._local_id) === lid) return true;
    if (cid && so._change_id && String(so._change_id) === cid && String(so.id) !== lid) return true;
  }
  return false;
}

export function isLocalOrderId(id) {
  return typeof id === "string" && id.startsWith("local-");
}

/**
 * Remove optimistic `local-…` placeholders from `orders` whenever their
 * cloud copy is present in the same list.
 */
export function dedupeLocalOrders(orders) {
  const arr = Array.isArray(orders) ? orders : [];
  return arr.filter((o) => !(o && isLocalOrderId(o.id) && serverHasLocalOrder(o, arr)));
}

/**
 * Insert `serverOrder` into `orders`: if an optimistic `local-…` placeholder
 * for it exists (same _local_id / _change_id) the placeholder is replaced
 * in place, otherwise the order is appended. Always returns a deduped list.
 */
export function upsertServerOrder(orders, serverOrder) {
  const arr = Array.isArray(orders) ? orders : [];
  if (!serverOrder || !serverOrder.id) return arr;
  if (arr.some((o) => o && String(o.id) === String(serverOrder.id))) return dedupeLocalOrders(arr);
  const lid = serverOrder._local_id ? String(serverOrder._local_id) : "";
  const cid = serverOrder._change_id ? String(serverOrder._change_id) : "";
  const idx = arr.findIndex((o) => o && isLocalOrderId(o.id) && (
    (lid && String(o.id) === lid) ||
    (cid && o._change_id && String(o._change_id) === cid)
  ));
  const next = idx >= 0
    ? arr.map((o, i) => (i === idx ? { ...o, ...serverOrder, _local_only: undefined } : o))
    : [...arr, serverOrder];
  return dedupeLocalOrders(next);
}

/**
 * Merge a local session with a server session. Orders are merged
 * recursively. Session metadata comes from server (latest truth).
 */
export function mergeSession(localSession, serverSession, options = {}) {
  if (!localSession) return serverSession;
  if (!serverSession) return localSession;
  // Session 73 — a row whose event is STILL QUEUED on this device (LOCAL /
  // PENDING in the sync queue) is not a ghost, however old: slow wifi or a
  // failing sync batch must never make a waiter's round vanish from view.
  const isQueued = typeof options.isQueuedOrder === "function" ? options.isQueuedOrder : () => false;

  const localOrders = localSession.orders || [];
  const serverOrders = serverSession.orders || [];
  const serverOrderMap = new Map(serverOrders.map((o) => [o.id, o]));
  const localOrderMap = new Map(localOrders.map((o) => [o.id, o]));
  const mergedOrders = [];
  const seenOrderIds = new Set();

  // Merge orders present in server
  for (const serverOrder of serverOrders) {
    seenOrderIds.add(serverOrder.id);
    const localOrder = localOrderMap.get(serverOrder.id);
    mergedOrders.push(mergeOrder(localOrder, serverOrder));
  }

  // Add local-only orders (from WS events not yet in server snapshot) —
  // but never an optimistic placeholder whose cloud copy is already in the
  // server list (matched on _local_id / _change_id). Session 31.18.
  //
  // Session 62 — GHOSTS EXPIRE. A device-only order that the server still
  // does not know about after a few minutes is not "in flight", it is a
  // ghost (a refused order, a mesh copy of something that never synced…).
  // Kept forever it inflated one table's items + fee on one phone only
  // ("Partially paid · R88 due" on a table the server had settled). Keep a
  // device-only order only while it can still be in flight:
  //   • an optimistic `local-…` order less than 10 min old (sync pending), or
  //   • any order less than 90 s old (WS landed before the snapshot).
  // And never on a table the server has already settled.
  const nowMs = Date.now();
  const serverSettled = Boolean(serverSession.payment_recorded_at);
  const ageMs = (o) => {
    const t = Date.parse(o.created_at || o.updated_at || "");
    return Number.isFinite(t) ? nowMs - t : Number.POSITIVE_INFINITY;
  };
  for (const localOrder of localOrders) {
    if (seenOrderIds.has(localOrder.id)) continue;
    if (serverHasLocalOrder(localOrder, serverOrders)) continue;
    const age = ageMs(localOrder);
    const queued = isQueued(localOrder);
    const inFlight = queued || (isLocalOrderId(localOrder.id) ? age < 10 * 60 * 1000 : age < 90 * 1000);
    if (!inFlight) continue;
    if (serverSettled && !isLocalOrderId(localOrder.id) && !queued) continue;
    mergedOrders.push(localOrder);
  }

  // Use server session as base (freshest metadata like table_number, status, etc.)
  // but with merged orders
  return {
    ...serverSession,
    orders: dedupeLocalOrders(mergedOrders),
    // Preserve local-only fields
    _wsSeq: localSession._wsSeq,
  };
}

// ─── Top-level table-sessions merge ──────────────────────────────────

// Session 72 — how long an optimistic row (a `local-…` order, a table opened
// with a client-side id) may survive an AUTHORITATIVE server snapshot that
// does not list it. Long enough for the sync loop to deliver it over a slow
// edge, short enough that a refused / lost event can never become a ghost.
export const OPTIMISTIC_SURVIVAL_MS = 60 * 1000;

function _rowAgeMs(row, nowMs) {
  const t = Date.parse(row?.created_at || row?.opened_at || row?.updated_at || "");
  return Number.isFinite(t) ? nowMs - t : Number.POSITIVE_INFINITY;
}

/**
 * Session 72 — Recompute the server-derived counters a Staff table chip
 * reads (`pending_orders`, `confirmed_orders`, `completed_orders`,
 * `order_count`, `total_items`) from the session's CURRENT orders array.
 * WS deltas change `orders[]` but used to leave these counters as the last
 * poll left them, so two devices showed "1 pending" vs "0 pending" for the
 * same table until the next poll. Returns the same reference when nothing
 * changed so React.memo / identity shortcuts keep working.
 */
export function withRecountedOrders(session) {
  if (!session || !Array.isArray(session.orders)) return session;
  const orders = session.orders.filter(Boolean);
  const live = orders.filter((o) => !(o.voided === true || isTerminalOrderStatus(o.status)));
  const status = (o) => String(o.status || "").toLowerCase();
  const pending = orders.filter((o) => status(o) === "pending").length;
  const confirmed = orders.filter((o) => status(o) === "confirmed").length;
  const completed = orders.filter((o) => status(o) === "completed").length;
  const totalItems = live.reduce((n, o) => n + (o.items || []).reduce((k, it) => (it && !it.voided ? k + (Number(it.quantity) || 0) : k), 0), 0);
  // Money: only when every live order carries a numeric total (optimistic
  // placeholders do) — otherwise leave the server figure untouched.
  const allTotals = live.every((o) => Number.isFinite(Number(o.total)));
  const totalValue = allTotals ? Math.round(live.reduce((n, o) => n + Number(o.total), 0) * 100) / 100 : session.total_value;
  if (
    session.pending_orders === pending && session.confirmed_orders === confirmed &&
    session.completed_orders === completed && session.order_count === orders.length &&
    session.total_items === totalItems && session.total_value === totalValue
  ) return session;
  return { ...session, pending_orders: pending, confirmed_orders: confirmed, completed_orders: completed, order_count: orders.length, total_items: totalItems, total_value: totalValue };
}

/**
 * Session 72 — AUTHORITATIVE reconciliation: the server snapshot REPLACES
 * local state. Used when this device has been quiet (no optimistic action
 * and no WS-applied delta in the last few seconds before the fetch started),
 * i.e. when every mutation it knows about must already be in the snapshot.
 *
 * The only local rows that survive are optimistic ones still plausibly in
 * flight: `local-…` orders and client-id tables younger than
 * OPTIMISTIC_SURVIVAL_MS whose cloud copy the server does not list yet.
 * Everything else — stale statuses, ghost orders, tables closed elsewhere,
 * counters — comes from the server. This is the rule that guarantees every
 * staff dashboard converges to the same picture within one poll.
 */
export function replaceWithServerSessions(localSessions, serverSessions, nowMs = Date.now(), options = {}) {
  // Session 73 — rows still queued in this device's sync queue survive
  // regardless of age (see mergeSession). The 60 s cap only applies to
  // rows whose event is gone (synced, rejected or lost).
  const isQueuedOrder = typeof options.isQueuedOrder === "function" ? options.isQueuedOrder : () => false;
  const isQueuedSession = typeof options.isQueuedSession === "function" ? options.isQueuedSession : () => false;
  const localArr = localSessions || [];
  const serverArr = (serverSessions || []).filter(Boolean);
  const localMap = new Map(localArr.filter(Boolean).map((s) => [String(s.id), s]));
  const serverIds = new Set(serverArr.map((s) => String(s.id)));
  const serverAliasIds = new Set(serverArr.flatMap((s) => [s._local_id, s._change_id, ...(Array.isArray(s._local_ids) ? s._local_ids : [])]).filter(Boolean).map(String));

  const out = serverArr.map((serverSess) => {
    const localSess = localMap.get(String(serverSess.id));
    if (!localSess || !Array.isArray(localSess.orders) || localSess.orders.length === 0) return serverSess;
    const serverOrders = (serverSess.orders || []).filter(Boolean);
    // Optimistic orders the server has not confirmed yet survive briefly.
    const survivors = localSess.orders.filter((o) => (
      o &&
      !serverOrders.some((so) => String(so.id) === String(o.id)) &&
      !serverHasLocalOrder(o, serverOrders) &&
      (isQueuedOrder(o) || (isLocalOrderId(o.id) && _rowAgeMs(o, nowMs) < OPTIMISTIC_SURVIVAL_MS))
    ));
    if (survivors.length === 0) return serverSess;
    return { ...serverSess, orders: dedupeLocalOrders([...serverOrders, ...survivors]) };
  });

  // Tables opened on this device whose cloud copy is not listed yet.
  for (const localSess of localArr) {
    if (!localSess) continue;
    const id = String(localSess.id);
    if (serverIds.has(id) || serverAliasIds.has(id)) continue;
    const optimistic = !/^[0-9a-f]{24}$/i.test(id);
    if (isQueuedSession(localSess) || (optimistic && _rowAgeMs(localSess, nowMs) < OPTIMISTIC_SURVIVAL_MS)) out.push(localSess);
  }
  return out;
}

/**
 * Merge local tableSessions state with a server snapshot.
 *
 * Rules:
 * 1. Sessions in both → merge at session/order/item level
 * 2. Sessions only in server → add (new session we didn't know about)
 * 3. Sessions only in local → keep if recently added by WS (grace period),
 *    otherwise drop (server says it doesn't exist)
 *
 * @param {Array} localSessions - Current local state
 * @param {Array} serverSessions - Server snapshot from HTTP
 * @param {number} serverEventSeq - The event_seq from the server response
 * @param {number} localLastSeq - The client's lastSeqRef (last WS event processed)
 * @returns {Array} Merged sessions
 */
export function mergeTableSessions(localSessions, serverSessions, serverEventSeq, localLastSeq, options = {}) {
  const localArr = localSessions || [];
  const serverArr = serverSessions || [];
  const serverMap = new Map(serverArr.map((s) => [s.id, s]));
  const localMap = new Map(localArr.map((s) => [s.id, s]));
  const merged = [];
  const seenIds = new Set();
  const isInFlight = typeof options.isInFlight === "function" ? options.isInFlight : null;

  // Process all server sessions
  for (const serverSess of serverArr) {
    seenIds.add(serverSess.id);
    const localSess = localMap.get(serverSess.id);
    if (!localSess) {
      // New from server
      merged.push(serverSess);
      continue;
    }
    // Both exist — merge
    merged.push(mergeSession(localSess, serverSess, { isQueuedOrder: options.isQueuedOrder }));
  }

  // Handle local-only sessions
  for (const localSess of localArr) {
    if (seenIds.has(localSess.id)) continue;
    // Local session not in server response.
    // If our WS state is ahead of the server snapshot, keep it (grace).
    // This handles: WS created a session that the server snapshot
    // was built before the write committed.
    //
    // Session 62 — the grace is TIME-BOUNDED. "WS ahead of snapshot" is a
    // race that resolves within seconds; it was also true for hours after
    // every server restart (seq counter reset), which kept stale tables
    // on a device indefinitely. When the caller can tell us whether the
    // session is still plausibly in flight, that decides; otherwise fall
    // back to the seq comparison.
    if (isInFlight ? isInFlight(localSess) : localLastSeq > serverEventSeq) {
      merged.push(localSess);
    }
    // Otherwise: server is authoritative and doesn't have this session → drop it
    // (it was closed/removed)
  }

  return merged;
}

// ─── Runner: merge ready orders ──────────────────────────────────────
/**
 * Merge local ready orders with server snapshot.
 * Same principle: never downgrade item status.
 *
 * @param {Array} localOrders - Current local orders state
 * @param {Array} serverOrders - Server snapshot from /staff/ready-orders
 * @param {number} serverEventSeq - event_seq from server response
 * @param {number} localLastSeq - Runner's lastSeqRef
 * @param {Map} deliveredIdsMap - Recently-delivered order IDs to suppress
 * @returns {Array} Merged orders
 */
export function mergeReadyOrders(localOrders, serverOrders, serverEventSeq, localLastSeq, deliveredIdsMap) {
  const localArr = localOrders || [];
  const serverArr = serverOrders || [];
  const serverMap = new Map(serverArr.map((o) => [o.id, o]));
  const localMap = new Map(localArr.map((o) => [o.id, o]));
  const merged = [];
  const seenIds = new Set();

  // Process server orders
  for (const serverOrder of serverArr) {
    // Skip recently-delivered (optimistic suppression)
    if (deliveredIdsMap && deliveredIdsMap.has(serverOrder.id)) continue;

    seenIds.add(serverOrder.id);
    const localOrder = localMap.get(serverOrder.id);
    if (!localOrder) {
      merged.push(serverOrder);
      continue;
    }
    // Merge — keep more advanced item statuses
    merged.push(mergeOrder(localOrder, serverOrder));
  }

  // Keep local-only orders if WS state is ahead of server
  const nowMs = Date.now();
  for (const localOrder of localArr) {
    if (seenIds.has(localOrder.id)) continue;
    if (deliveredIdsMap && deliveredIdsMap.has(localOrder.id)) continue;
    // If local WS is ahead, keep (WS added this order but server snapshot
    // was stale). Session 69 — TIME-BOUNDED: under heavy load the WS seq is
    // practically always ahead of the HTTP snapshot, which kept cards the
    // server had long since removed on screen indefinitely.
    if (recentlyArrivedOverWs(localOrder, nowMs)) {
      merged.push(localOrder);
    }
    // Otherwise: server doesn't have it → it's been delivered or removed
  }

  return merged;
}

// Session 69 — how long a ticket that arrived over the WebSocket may stay on
// a station screen while the HTTP snapshot does not list it yet. Covers the
// real race (snapshot built before the write committed, ~ms to a few s) and
// a congested backend (slow writes, retried requests) without ever turning
// into "forever".
export const WS_AHEAD_GRACE_MS = 15 * 1000; // Session 73 — was 45 s; the HTTP snapshot is at most ~2 s stale

// Session 72 — grace windows for the AUTHORITATIVE station replace (see
// replaceStationOrders). Much tighter than the merge path: the list cache
// is invalidated on every broadcast, so a snapshot fetched ≥2.5 s after
// the last WS event already contains that event's write.
export const STATION_WS_SURVIVAL_MS = 10 * 1000;
export const STATION_MESH_SURVIVAL_MS = 10 * 60 * 1000;

/**
 * Session 72 — ONE SOURCE OF TRUTH for the Kitchen / Bar / Runner lists.
 * When the station has been quiet (no local tick, no WS delta) for a few
 * seconds before the fetch went out, the server list REPLACES local state
 * — item statuses included, so a "ready" the cloud never accepted (lost
 * offline event, rejected replay) can no longer hide a ticket here while
 * every other screen still shows it cooking, and a ticket cleared on
 * another device never lingers.
 *
 * Survivors (bounded): ids in `suppressMap` (this device's own just-sent
 * tickets, ≤15 s), WS-added tickets younger than STATION_WS_SURVIVAL_MS,
 * and LAN-mesh tickets younger than STATION_MESH_SURVIVAL_MS whose cloud
 * copy (`_change_id`) is not listed yet.
 */
export function replaceStationOrders(localOrders, serverOrders, suppressMap = null, nowMs = Date.now()) {
  const localArr = (localOrders || []).filter(Boolean);
  const serverArr = (serverOrders || []).filter(Boolean);
  const out = [];
  const seen = new Set();
  for (const so of serverArr) {
    if (suppressMap && suppressMap.has(so.id)) continue;
    seen.add(String(so.id));
    out.push(so);
  }
  const serverChangeIds = new Set(serverArr.map((o) => o && o._change_id).filter(Boolean).map(String));
  for (const lo of localArr) {
    if (seen.has(String(lo.id))) continue;
    if (lo._change_id && serverChangeIds.has(String(lo._change_id))) continue;
    if (suppressMap && suppressMap.has(lo.id)) continue;
    const wsAt = Number(lo._ws_added_at);
    if (Number.isFinite(wsAt) && wsAt > 0 && nowMs - wsAt <= STATION_WS_SURVIVAL_MS) { out.push(lo); continue; }
    if (lo._via_mesh && lo._change_id) {
      const ageMs = nowMs - (Date.parse(lo.created_at || "") || nowMs);
      if (ageMs < STATION_MESH_SURVIVAL_MS) out.push(lo);
    }
  }
  return dedupeLocalOrders(out);
}

function recentlyArrivedOverWs(order, nowMs) {
  if (!order) return false;
  const at = Number(order._ws_added_at);
  return Number.isFinite(at) && at > 0 && nowMs - at <= WS_AHEAD_GRACE_MS;
}

// ─── Kitchen/Bar: merge department orders ────────────────────────────
/**
 * Merge local kitchen/bar orders with server snapshot.
 * Never downgrade item statuses; never remove orders that local WS
 * shows as still pending but server snapshot is stale.
 *
 * @param {Array} localOrders - Current local state
 * @param {Array} serverOrders - Server snapshot
 * @param {number} serverEventSeq - event_seq from server response
 * @param {number} localLastSeq - Dashboard's lastSeqRef
 * @param {Map} recentlyReadyMap - Recently-readied order IDs to suppress
 * @returns {Array} Merged orders
 */
export function mergeDeptOrders(localOrders, serverOrders, serverEventSeq, localLastSeq, recentlyReadyMap) {
  const localArr = localOrders || [];
  const serverArr = serverOrders || [];
  const serverMap = new Map(serverArr.map((o) => [o.id, o]));
  const localMap = new Map(localArr.map((o) => [o.id, o]));
  const merged = [];
  const seenIds = new Set();

  const nowMs = Date.now();

  for (const serverOrder of serverArr) {
    if (recentlyReadyMap && recentlyReadyMap.has(serverOrder.id)) continue;
    seenIds.add(serverOrder.id);
    const localOrder = localMap.get(serverOrder.id);
    if (!localOrder) {
      merged.push(serverOrder);
      continue;
    }
    const mergedOrder = mergeOrder(localOrder, serverOrder);
    // Session 69 — the server list is authoritative for which lines still
    // exist unless this ticket only just arrived over the WS (the snapshot
    // may predate it). Seq comparison dropped: see the local-only loop.
    const serverIsFresh = !recentlyArrivedOverWs(localOrder, nowMs) || !(localLastSeq > serverEventSeq);
    // Session 31.13 — The station list never carries voided lines, so a
    // local line that the (fresh) server ticket no longer has was voided:
    // drop it instead of preserving it as a "WS-ahead" extra. Lines with
    // no stable id are kept (positional merge already handled them).
    if (serverIsFresh && Array.isArray(mergedOrder.items)) {
      const serverIds = new Set((serverOrder.items || []).map((it) => itemId(it)).filter((id) => !isEmptyId(id)));
      const pruned = mergedOrder.items.filter((it) => {
        if (!it || it.voided) return false;
        const id = itemId(it);
        return isEmptyId(id) || serverIds.has(id);
      });
      if (pruned.length !== mergedOrder.items.length) mergedOrder.items = pruned;
      if (pruned.length === 0) continue; // nothing left for this station
    }
    merged.push(mergedOrder);
  }

  // Session 30.26 — Optimistic tickets that arrived over the LAN mesh
  // (`_via_mesh`) are not on the server yet. Keep them until the cloud
  // shows an order with the same `_change_id` (the authoritative copy,
  // which then replaces them) — otherwise a kitchen fetch that races
  // the staff device's sync would make the ticket vanish mid-cook.
  const serverChangeIds = new Set(serverArr.map((o) => o && o._change_id).filter(Boolean));

  for (const localOrder of localArr) {
    if (seenIds.has(localOrder.id)) continue;
    if (recentlyReadyMap && recentlyReadyMap.has(localOrder.id)) continue;
    // Session 69 — the "WS is ahead of the snapshot" grace is TIME-BOUNDED
    // (see recentlyArrivedOverWs). With 16 waiters firing orders the WS
    // seq outran every HTTP snapshot, so this branch was always true and
    // a ticket the server no longer listed — delivered elsewhere, voided,
    // or a card primed from a stale local cache that never belonged to
    // this station — stayed on the kitchen/bar screen for the whole
    // service. Cards without a `_ws_added_at` stamp (cache / IndexedDB
    // priming) get no grace at all: the server list is authoritative.
    // The seq comparison is deliberately NOT part of this test any more:
    // with several backend instances the HTTP snapshot and the WebSocket
    // can come from different processes whose counters are unrelated.
    if (recentlyArrivedOverWs(localOrder, nowMs)) {
      merged.push(localOrder);
      continue;
    }
    if (localOrder._via_mesh && localOrder._change_id && !serverChangeIds.has(localOrder._change_id)) {
      const ageMs = nowMs - (Date.parse(localOrder.created_at || "") || nowMs);
      if (ageMs < 6 * 60 * 60 * 1000) merged.push(localOrder);
    }
  }

  // Session 31.24 — final safety net: never return two rows for the same
  // logical order (mesh copy + cloud copy that slipped past the id/seq
  // guards because their change_ids differed). This is what previously
  // showed a drinks ticket twice on the bar until a manual refresh.
  return dedupeByIdentity(merged);
}
